/**
 * MO-06 purchase safeguards, client side (the API enforces the same):
 * COPPA accounts cannot buy without a verified parent (SY-02.1), and spending
 * over the daily threshold ($100) needs a one-time "Are you sure?".
 */
import type { StoreResponse } from '../schemas.js';
import type { IapProduct, IapProvider, PurchaseOptions, PurchaseResult } from './types.js';

export interface PurchaseGuardState {
  coppa: boolean;
  parentVerified: boolean;
  /** StoreResponse.purchasesBlocked (remote config or server-side block) */
  purchasesBlocked?: boolean;
}

export type PurchaseBlock = 'coppa' | 'disabled';

export function purchaseBlock(s: PurchaseGuardState): PurchaseBlock | null {
  if (s.coppa && !s.parentVerified) return 'coppa';
  if (s.purchasesBlocked) return 'disabled';
  return null;
}

/**
 * True when this purchase would take today's spend over the confirm threshold. Open checkout
 * pages count toward it (StoreResponse.heldTodayUsdCents), because the API's guard counts them:
 * a player holding two abandoned $49.99 pages must be offered the "Are you sure?" and not the
 * 409 confirm_required that the screen can only show as `store.error.blocked`.
 *
 * `heldTodayUsdCents` is read defensively although the type says it is required. The screen gets
 * this object from GET /v1/store through an unchecked cast, so a client that ships ahead of the
 * API deploy would read `undefined` here — and `undefined` in the sum makes it NaN, which is
 * greater than nothing, so the dialog would stop opening for ANY player, including the ones whose
 * charges alone already cross the threshold. Losing the whole confirmation to a release-order
 * mistake is worse than the gap this field closes, so a missing one counts as no holds.
 */
export function needsSpendConfirm(store: Pick<StoreResponse, 'spentTodayUsdCents' | 'confirmOverUsdCents'> & Partial<Pick<StoreResponse, 'heldTodayUsdCents'>>, priceUsdCents: number | null): boolean {
  return priceUsdCents !== null && store.spentTodayUsdCents + (store.heldTodayUsdCents ?? 0) + priceUsdCents > store.confirmOverUsdCents;
}

/** Wraps any IapProvider: blocked purchases never reach the platform sheet. */
export class GuardedIap implements IapProvider {
  constructor(
    private readonly inner: IapProvider,
    private readonly state: () => PurchaseGuardState,
  ) {}

  get provider(): IapProvider['provider'] {
    return this.inner.provider;
  }

  products(): Promise<IapProduct[]> {
    return this.inner.products();
  }

  async purchase(productId: string, opts: PurchaseOptions = {}): Promise<PurchaseResult> {
    const block = purchaseBlock(this.state());
    if (block) return { status: 'blocked', provider: this.inner.provider, productId, reason: block };
    return this.inner.purchase(productId, opts);
  }

  restore(): Promise<PurchaseResult[]> {
    return this.inner.restore();
  }

  resume(): Promise<PurchaseResult | null> {
    return this.inner.resume();
  }
}
